import { z } from 'zod/v4'

import { defineScreen, type ScreenData } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Configuraciones fiscales» (`admin.taxConfigs`): one SUMMARY row per country and year, and the catalogue of countries
 * that have a configuration. The rules of each configuration (`config`) do not travel: the form that edits one reads
 * its detail from its own route. Strict: a field that is not named here does not travel.
 */

export const adminTaxConfigRowSchema = z.strictObject({
  country: z.string().length(2),
  year: z.number().int(),
  currency: z.string().min(1),
  isActive: z.boolean(),
  isPreset: z.boolean(),
  lastUpdated: z.iso.datetime(),
})

export const adminTaxCountrySchema = z.strictObject({
  code: z.string(),
  name: z.string(),
  hasActiveConfiguration: z.boolean(),
  /** Absent means «not known» (the template query failed): the page stays silent, it never reads it as «no template». */
  hasPdfTemplate: z.boolean().optional(),
})

export const adminTaxConfigsScreen = defineScreen({
  id: 'admin.taxConfigs',
  path: '/api/screens/admin/tax-configs',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    configs: screenSection(z.array(adminTaxConfigRowSchema)),
    countries: screenSection(z.array(adminTaxCountrySchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.taxConfigs'],
  budget: { requests: 1, statements: 10, bytes: 20_000, p95Ms: 250 },
  summary: 'Tax configurations: one summary row per country and year, and the country catalogue',
})

export type AdminTaxConfigRow = z.infer<typeof adminTaxConfigRowSchema>
export type AdminTaxCountry = z.infer<typeof adminTaxCountrySchema>
export type AdminTaxConfigsData = ScreenData<typeof adminTaxConfigsScreen>
